import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Box, TextField, Typography } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Formik } from "formik";
import * as yup from "yup";
import { GraphQLClient } from "graphql-request";

import Header from "../../components/Header";
import ActionButton from "../../components/ActionButton";
import TableActions from "../../components/TableActions";
import DeleteProjectModal from "../../components/DeleteProjectModal";
import MarkdownEditor from "../../components/MarkdownEditor";
import EntitySelectModal from "../../components/EntitySelectModal.jsx";
import AddTransitionModal from "../../components/AddTransitionModal";
import { useSnackbar } from "../../components/snackbarProvider.jsx";
import { useTenant } from "../../context/TenantContext";

import { graphqlEndpoint } from "../../config";

import { READ_PLAYBOOK, READ_PLAYBOOKS } from "../../graph/playbooks/queries";
import { DELETE_PROJECT } from "../../graph/projects/mutations";
import {
  UPDATE_PLAYBOOK,
  ADD_PLAYBOOK_ITEM,
  REMOVE_PLAYBOOK_ITEM,
  ADD_PLAYBOOK_TRANSITION,
  REMOVE_PLAYBOOK_TRANSITION,
} from "../../graph/playbooks/mutations";

import { READ_PROJECTS_FILTER } from "../../graph/projects/queries";

const client = new GraphQLClient(graphqlEndpoint);



const validationSchema = yup.object({
  name: yup.string().required("Name is required"),
});

const EditPlaybookForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { tenantId, tenantName } = useTenant();
  const { setSnackbar } = useSnackbar();

  const [addTransitionModalOpen, setAddTransitionModalOpen] = useState(false);
  const redirectPath = location.state?.redirectPath || "/managePlaybooks";

  const [initialValues, setInitialValues] = useState(null);
  const [items, setItems] = useState([]);
  const [allSops, setAllSops] = useState([]);
  const [allPlaybooks, setAllPlaybooks] = useState([]);
  const [transitions, setTransitions] = useState([]);

  const [addModalOpen, setAddModalOpen] = useState(false);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  

  // =========================
  // NAVIGATION
  // =========================

  const handleEditSop = (sopId) => {
    navigate(`/sops/edit/${sopId}`, {
      state: {
        redirectPath: location.pathname,
      },
    });
  };

  const handleCosts = (sopId) => {
    navigate(`/manageProjects/${sopId}/costs`, {
      state: {
        redirectPath: location.pathname,
        source: "playbook",
      },
    });
  };

  const handleCreateTask = (sopId) => {
    navigate("/createTask", {
      state: {
        projectId: sopId,
        redirectPath: location.pathname,
      },
    });
  };

  const handleCreateSop = () => {
    navigate("/createProject", {
      state: {
        sop: true,
        name: "SOP",
        playbookId: id,
        redirectPath: location.pathname,
      },
    });
  };

  // =========================
  // LOAD DATA
  // =========================

  const load = async () => {
    try {
      const [{ readPlaybook }, { readPlaybooks }, { readProjectsFilter }] =
        await Promise.all([
          client.request(READ_PLAYBOOK, { id }),
          client.request(READ_PLAYBOOKS, { tenantId }),
          client.request(READ_PROJECTS_FILTER, {
            filter: {
              tenantId,
              isSOPTemplate: true,
            },
          }),
        ]);

      if (!readPlaybook) {
        setSnackbar({
          open: true,
          severity: "error",
          message: "Playbook not found",
        });
        navigate(redirectPath);
        return;
      }

      setInitialValues({
        id: readPlaybook.id,
        name: readPlaybook.name || "",
        objective: readPlaybook.objective || "",
        description: readPlaybook.description || "",
      });

      setItems(readPlaybook.items || []);
      setAllPlaybooks(readPlaybooks || []);
      setTransitions(readPlaybook.transitions || []);
      const formattedSops = (readProjectsFilter || []).map((sop) => ({
        ...sop,
        taskCount:
          sop.tasks?.filter((task) => task.isLatest !== false).length || 0,
      }));

      setAllSops(formattedSops);
    } catch (err) {
      console.error(err);

      setSnackbar({
        open: true,
        severity: "error",
        message: "Failed to load playbook",
      });
    }
  };

  useEffect(() => {
    if (tenantId) {
      load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, id]);

  // =========================
  // FILTER AVAILABLE SOPS
  // =========================

  const availableSops = useMemo(() => {
    const linkedSopIds = new Set(
      allPlaybooks
        .flatMap((playbook) => playbook.items || [])
        .map((item) => item.sopId ?? item.sop?.id)
        .filter(Boolean),
    );

    return allSops.filter((sop) => !linkedSopIds.has(sop.id));
  }, [allPlaybooks, allSops]);

  // =========================
  // UPDATE PLAYBOOK
  // =========================

  const handleSubmit = async (values) => {
    try {
      await client.request(UPDATE_PLAYBOOK, {
        input: {
          id: values.id,
          name: values.name,
          objective: values.objective || null,
          description: values.description || null,
        },
      });

      setSnackbar({
        open: true,
        severity: "success",
        message: "Playbook updated",
      });

      navigate(redirectPath);
    } catch (err) {
      console.error(err);

      setSnackbar({
        open: true,
        severity: "error",
        message: "Failed to update playbook",
      });
    }
  };

  // =========================
  // ADD PLAYBOOK ITEM
  // =========================

  const handleAddItem = async (sopId) => {
    try {
      const sequenceStep = 1024;

      const lastSequence = items.length
        ? Math.max(...items.map((item) => parseInt(item.sequence || "0", 10)))
        : 0;

      const nextSequence = String(lastSequence + sequenceStep).padStart(7, "0");

      await client.request(ADD_PLAYBOOK_ITEM, {
        input: {
          playbookId: id,
          sopId,
          sequence: nextSequence,
        },
      });

      setSnackbar({
        open: true,
        severity: "success",
        message: "SOP added to playbook",
      });

      setAddModalOpen(false);
      await load();
    } catch (err) {
      console.error(err);

      setSnackbar({
        open: true,
        severity: "error",
        message: "Failed to add SOP",
      });
    }
  };

  // =========================
  // REMOVE PLAYBOOK ITEM
  // =========================

  const handleDeleteItem = async (itemId) => {
    try {
      await client.request(REMOVE_PLAYBOOK_ITEM, {
        input: {
          id: itemId,
        },
      });

      setSnackbar({
        open: true,
        severity: "success",
        message: "SOP removed from playbook",
      });

      await load();
    } catch (err) {
      console.error(err);

      setSnackbar({
        open: true,
        severity: "error",
        message: "Failed to remove SOP from playbook",
      });
    }
  };

  // =========================
  // ADD TRANSITION
  // =========================

  const handleAddTransition = async (values) => {
    try {
      await client.request(ADD_PLAYBOOK_TRANSITION, {
        input: {
          playbookId: id,
          fromPlaybookItemId: values.fromPlaybookItemId,
          toPlaybookItemId: values.toPlaybookItemId,
          outcomeKey: values.outcomeKey,
          label: values.label,
          isDefault: values.isDefault,
        },
      });

      setSnackbar({
        open: true,
        severity: "success",
        message: "Transition added",
      });

      setAddTransitionModalOpen(false);
      await load();
    } catch (err) {
      console.error(err);

      setSnackbar({
        open: true,
        severity: "error",
        message: "Failed to add transition",
      });
    }
  };

  // =========================
  // REMOVE TRANSITION
  // =========================

  const handleRemoveTransition = async (transitionId) => {
    try {
      await client.request(REMOVE_PLAYBOOK_TRANSITION, {
        input: {
          id: transitionId,
        },
      });

      setSnackbar({
        open: true,
        severity: "success",
        message: "Transition removed",
      });

      await load();
    } catch (err) {
      console.error(err);

      setSnackbar({
        open: true,
        severity: "error",
        message: "Failed to remove transition",
      });
    }
  };

  // =========================
  // TABLE COLUMNS
  // =========================

  const columns = [
    {
      field: "sequence",
      headerName: "Seq",
      flex: 0.4,
      renderCell: ({ value }) => {
        if (!value) {
          return "—";
        }

        return parseInt(value, 10) / 1024;
      },
    },
    {
      field: "sopId",
      headerName: "SOP",
      flex: 1.5,
      renderCell: ({ row }) => {
        const sop = allSops.find((item) => item.id === row.sopId);

        return sop?.title || row.sop?.title || "Unknown SOP";
      },
    },
    {
      field: "taskCount",
      headerName: "Tasks",
      flex: 0.5,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => {
        const sop = allSops.find((item) => item.id === row.sopId);

        return sop?.taskCount ?? row.sop?.taskCount ?? 0;
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => (
        <TableActions
          onCosts={() => handleCosts(row.sopId)}
          onEdit={() => handleEditSop(row.sopId)}
          onDelete={() => handleOpenDeleteModal(row)}
          onCreateTask={() => handleCreateTask(row.sopId)}
        />
      ),
    },
  ];

  const handleOpenDeleteModal = (row) => {
    const sop = allSops.find((project) => project.id === row.sopId);

    if (!sop) {
      setSnackbar({
        open: true,
        severity: "error",
        message: "Unable to find SOP",
      });
      return;
    }

    setSelectedProject({
      ...sop,
      playbookItemId: row.id,
    });

    setDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setDeleteModalOpen(false);
    setSelectedProject(null);
  };

  const handleConfirmDelete = async () => {
    if (!selectedProject?.id) {
      return;
    }

    try {
      await client.request(DELETE_PROJECT, {
        id: selectedProject.id,
      });

      if (selectedProject.playbookItemId) {
        await client.request(REMOVE_PLAYBOOK_ITEM, {
          input: {
            id: selectedProject.playbookItemId,
          },
        });
      }

      handleCloseDeleteModal();
      await load();

      setSnackbar({
        open: true,
        severity: "success",
        message: "SOP deleted successfully",
      });
    } catch (err) {
      console.error("Failed to delete SOP:", err);

      setSnackbar({
        open: true,
        severity: "error",
        message: err?.response?.errors?.[0]?.message || "Failed to delete SOP",
      });
    }
  };

  const transitionColumns = [
    {
      field: "from",
      headerName: "From",
      flex: 1.5,
      renderCell: ({ row }) => {
        const sequence = row.fromPlaybookItem?.sequence
          ? parseInt(row.fromPlaybookItem.sequence, 10) / 1024
          : "—";

        const title = row.fromPlaybookItem?.sop?.title || "Unknown SOP";

        return `${sequence}. ${title}`;
      },
    },
    {
      field: "to",
      headerName: "To",
      flex: 1.5,
      renderCell: ({ row }) => {
        const sequence = row.toPlaybookItem?.sequence
          ? parseInt(row.toPlaybookItem.sequence, 10) / 1024
          : "—";

        const title = row.toPlaybookItem?.sop?.title || "Unknown SOP";

        return `${sequence}. ${title}`;
      },
    },
    {
      field: "outcomeKey",
      headerName: "Outcome",
      flex: 1,
      renderCell: ({ value }) => value || "—",
    },
    {
      field: "label",
      headerName: "Label",
      flex: 1.2,
      renderCell: ({ value }) => value || "—",
    },
    {
      field: "isDefault",
      headerName: "Default",
      flex: 0.8,
      renderCell: ({ value }) => (value ? "Yes" : "No"),
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => (
        <ActionButton
          size="small"
          tone="danger"
          onClick={() => handleRemoveTransition(row.id)}
        >
          Remove
        </ActionButton>
      ),
    },
  ];

  // =========================
  // LOADING STATES
  // =========================

  if (!tenantId) {
    return <Typography>Select tenant first</Typography>;
  }

  if (!initialValues) {
    return <Typography>Loading...</Typography>;
  }

  return (
    <Box>
      <Header
        title="EDIT PLAYBOOK"
        subtitle={`Edit playbook for ${tenantName || ""}`}
      />

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit: submitForm,
          setFieldValue,
        }) => (
          <form onSubmit={submitForm}>
            <Box
              display="grid"
              gap="20px"
              gridTemplateColumns="repeat(4, minmax(0, 1fr))"
            >
              <TextField
                fullWidth
                label="Playbook Name"
                name="name"
                value={values.name}
                onChange={handleChange}
                onBlur={handleBlur}
                error={Boolean(touched.name && errors.name)}
                helperText={touched.name && errors.name}
                sx={{ gridColumn: "span 4" }}
              />

              <TextField
                fullWidth
                label="Objective"
                name="objective"
                multiline
                rows={2}
                value={values.objective}
                onChange={handleChange}
                onBlur={handleBlur}
                error={Boolean(touched.objective && errors.objective)}
                helperText={touched.objective && errors.objective}
                sx={{ gridColumn: "span 4" }}
              />

              <MarkdownEditor
                value={values.description}
                onChange={(nextValue) =>
                  setFieldValue("description", nextValue)
                }
                label="Description"
                minRows={14}
                error={Boolean(touched.description && errors.description)}
                helperText={touched.description && errors.description}
                sx={{ gridColumn: "span 4" }}
              />
            </Box>

            <Box display="flex" justifyContent="flex-end" mt="20px">
              <ActionButton type="submit" tone="success">
                Save Changes
              </ActionButton>
            </Box>
          </form>
        )}
      </Formik>

      {/* ========================= */}
      {/* PLAYBOOK ITEMS */}
      {/* ========================= */}

      <Box mt={4}>
        <Header title="PLAYBOOK ITEMS" subtitle="SOP sequence" />

        <Box display="flex" gap={1} mb={2}>
          <ActionButton tone="gold" onClick={handleCreateSop}>
            + Create SOP
          </ActionButton>

          <ActionButton tone="teal" onClick={() => setAddModalOpen(true)}>
            + Add SOP
          </ActionButton>
        </Box>

        <DataGrid
          autoHeight
          rows={items}
          columns={columns}
          disableRowSelectionOnClick
        />
      </Box>

      {/* ========================= */}
      {/* TRANSITIONS */}
      {/* ========================= */}

      <Box mt={4}>
        <Header title="TRANSITIONS" subtitle="Playbook transitions" />

        <ActionButton
          variant="contained"
          tone="teal"
          onClick={() => setAddTransitionModalOpen(true)}
          sx={{ mb: 2 }}
        >
          + Add Transition
        </ActionButton>

        <DataGrid
          autoHeight
          rows={transitions}
          columns={transitionColumns}
          disableRowSelectionOnClick
        />
      </Box>

      {/* ========================= */}
      {/* MODALS */}
      {/* ========================= */}

      <EntitySelectModal
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        entities={availableSops}
        getLabel={(sop) => sop.title}
        label="Add SOP to Playbook"
        onSubmit={handleAddItem}
      />

      <AddTransitionModal
        open={addTransitionModalOpen}
        onClose={() => setAddTransitionModalOpen(false)}
        items={items}
        allSops={allSops}
        onSubmit={handleAddTransition}
      />

      <DeleteProjectModal
        open={deleteModalOpen}
        onClose={handleCloseDeleteModal}
        onConfirm={handleConfirmDelete}
        project={selectedProject}
      />
    </Box>
  );
};

export default EditPlaybookForm;